import { useEffect, useRef, useState } from "react";
import { Plus, Loader2, Archive, ArchiveRestore, Trash2, Pencil } from "lucide-react";
import api, { errMsg } from "@/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { v: "musteri", l: "Müşteri" },
  { v: "tedarikci", l: "Tedarikçi" },
  { v: "gonderici", l: "Gönderici" },
  { v: "alici", l: "Alıcı" },
];

const EMPTY = {
  name: "", type: "musteri", tax_no: "", country: "TR", city: "", address: "",
  contact_name: "", contact_email: "", contact_phone: "", notes: "",
};

export default function Parties() {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const reqSeq = useRef(0);

  const startEdit = (r) => {
    setEditId(r.id);
    setForm({
      name: r.name || "", type: r.type || "musteri", tax_no: r.tax_no || "", country: r.country || "TR",
      city: r.city || "", address: r.address || "", contact_name: r.contact_name || "",
      contact_email: r.contact_email || "", contact_phone: r.contact_phone || "", notes: r.notes || "",
    });
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const load = () => {
    const seq = ++reqSeq.current;
    return api.get("/parties", { params: { type: filter || undefined, q: q || undefined, archived } })
      .then((r) => { if (seq === reqSeq.current) setRows(r.data); })
      .catch((e) => { if (seq === reqSeq.current) toast.error(errMsg(e)); });
  };

  useEffect(() => { load(); }, [filter, q, archived]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/parties/${editId}`, form);
        toast.success("Cari kayıt güncellendi");
      } else {
        await api.post("/parties", form);
        toast.success("Cari kayıt oluşturuldu");
      }
      setForm(EMPTY);
      setEditId(null);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  const archive = async (id) => {
    try {
      await api.post(`/parties/${id}/archive`);
      toast.success("Kayıt arşivlendi");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const unarchive = async (id) => {
    try {
      await api.post(`/parties/${id}/unarchive`);
      toast.success("Kayıt arşivden çıkarıldı");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`"${row.name}" kaydı kalıcı olarak silinecek. Bu işlem geri alınamaz. Devam edilsin mi?`)) return;
    try {
      await api.delete(`/parties/${row.id}`);
      toast.success("Kayıt kalıcı olarak silindi");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="parties-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline">Ana Kayıtlar</p>
          <h1 className="font-display text-4xl font-black tracking-tighter">Cari Kayıtlar</h1>
        </div>
        {can("party:write") && (
          <button className="brut-btn" onClick={() => setOpen(!open)} data-testid="new-party-button">
            <Plus className="h-4 w-4" /> Yeni Kayıt
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={save} className="brut animate-fade-up p-4 sm:p-6" data-testid="party-form">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="overline">Unvan</label>
              <input required className="brut-input mt-1.5" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="party-name-input" />
            </div>
            <div>
              <label className="overline">Tip</label>
              <select className="brut-input mt-1.5" value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="party-type-select">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>
            <div>
              <label className="overline">Vergi No</label>
              <input className="brut-input mt-1.5" value={form.tax_no}
                onChange={(e) => setForm({ ...form, tax_no: e.target.value })} data-testid="party-taxno-input" />
            </div>
            <div>
              <label className="overline">Ülke</label>
              <input className="brut-input mt-1.5" value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="party-country-input" />
            </div>
            <div>
              <label className="overline">Şehir</label>
              <input className="brut-input mt-1.5" value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="party-city-input" />
            </div>
            <div>
              <label className="overline">İletişim Kişisi</label>
              <input className="brut-input mt-1.5" value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="party-contact-input" />
            </div>
            <div>
              <label className="overline">E-posta</label>
              <input type="email" className="brut-input mt-1.5" value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="party-email-input" />
            </div>
            <div>
              <label className="overline">Telefon</label>
              <input className="brut-input mt-1.5" value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="party-phone-input" />
            </div>
            <div>
              <label className="overline">Adres</label>
              <input className="brut-input mt-1.5" value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="party-address-input" />
            </div>
          </div>
          <button type="submit" disabled={busy} className="brut-btn mt-5" data-testid="party-save-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {editId ? "Değişiklikleri Kaydet" : "Kaydet"}
          </button>
          {editId && (
            <button type="button" className="brut-btn-ghost ml-3 mt-5" data-testid="party-cancel-edit"
              onClick={() => { setEditId(null); setForm(EMPTY); setOpen(false); }}>
              Vazgeç
            </button>
          )}
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <select className="brut-input max-w-[200px]" value={filter} onChange={(e) => setFilter(e.target.value)}
          data-testid="party-filter-select">
          <option value="">Tüm tipler</option>
          {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <input className="brut-input max-w-[280px]" placeholder="Unvan ara..." value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="party-search-input" />
        <div className="flex border border-zinc-300">
          <button type="button" onClick={() => setArchived(false)} data-testid="party-view-active"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${!archived ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Aktif
          </button>
          <button type="button" onClick={() => setArchived(true)} data-testid="party-view-archived"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${archived ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Arşiv
          </button>
        </div>
        <span className="tnum font-mono text-xs text-zinc-500" data-testid="party-count">{rows.length} kayıt</span>
      </div>

      {archived && (
        <p className="brut px-4 py-3 text-xs text-zinc-600" data-testid="archive-info">
          Arşivdeki kayıtlar geri alınabilir. Kalıcı silme yalnızca yöneticiye açıktır ve kayıt hiçbir talep,
          teklif, operasyon, tarife veya kullanıcıya bağlı değilse mümkündür; bağlıysa kayıt arşivde tutulur.
          Operasyon dosyaları silinmez; iptal ve arşiv durumları kullanılır.
        </p>
      )}

      <div className="brut overflow-x-auto" data-testid="parties-table">
        <table className="dense w-full">
          <thead>
            <tr><th>Unvan</th><th>Tip</th><th>Şehir/Ülke</th><th>İletişim</th><th>E-posta</th><th className="!text-right">İşlem</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={i % 2 ? "bg-zinc-50" : ""} data-testid={`party-row-${r.id}`}>
                <td className="font-semibold">{r.name}</td>
                <td>{TYPES.find((t) => t.v === r.type)?.l}</td>
                <td>{r.city} {r.country}</td>
                <td>{r.contact_name || "-"}</td>
                <td className="font-mono text-xs">{r.contact_email || "-"}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    {can("party:write") && (
                      <button onClick={() => startEdit(r)} className="text-zinc-400 hover:text-klein"
                        data-testid={`edit-party-${r.id}`} title="Düzenle">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                    {can("party:write") && (archived ? (
                      <button onClick={() => unarchive(r.id)} className="text-zinc-400 hover:text-green-700"
                        data-testid={`unarchive-party-${r.id}`} title="Arşivden çıkar">
                        <ArchiveRestore className="h-4 w-4" />
                      </button>
                    ) : (
                      <button onClick={() => archive(r.id)} className="text-zinc-400 hover:text-yellow-700"
                        data-testid={`archive-party-${r.id}`} title="Arşivle">
                        <Archive className="h-4 w-4" />
                      </button>
                    ))}
                    {can("*") && (
                      <button onClick={() => remove(r)} className="text-zinc-400 hover:text-red-600"
                        data-testid={`delete-party-${r.id}`} title="Kalıcı olarak sil">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-zinc-500" data-testid="parties-empty-state">
                {archived ? "Arşivde kayıt yok." : "Kayıt bulunamadı."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
